import {
  ArrowDownward,
  ArrowUpward,
  AssistantOutlined,
  CalendarToday,
  Close,
  Deselect,
  Search,
  SortByAlpha,
  Star,
  StarBorder,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";

import { useGetUserDetails } from "src/helpers/UserDetails";
import { useGetAllConfigs } from "src/views/assistant/AssistantServices";
import AgentScopeToggle from "src/views/assistant/components/AgentScopeToggle";
import {
  AgentScope,
  filterByAgentScope,
} from "src/views/assistant/services/PersonaConfigurationService";
import { PersonaAPIData, Toolkit } from "src/views/assistant/types";

interface PersonaSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (persona: PersonaAPIData | null) => void;
  selectedPersona: PersonaAPIData | null;
  // When set, only personas with this toolkit active are shown, and the
  // "None" option is hidden (None has no toolkit, so it fails the same filter).
  requiredToolkitName?: string;
}

const PersonaSelectorModal = ({
  open,
  onClose,
  onConfirm,
  selectedPersona,
  requiredToolkitName,
}: PersonaSelectorModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "date" | "stars">("stars");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [scope, setScope] = useState<AgentScope>("all");
  const currentUser = useGetUserDetails();

  // Fetch personas with server-side search filtering
  const { data: personaList, isLoading } = useGetAllConfigs({
    searchQuery: debouncedSearchQuery,
    orderBy: "name",
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Clear search when modal opens
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setDebouncedSearchQuery("");
    }
  }, [open]);

  // Server handles search filtering; when a toolkit is required (e.g. the
  // Equipment AI ER chat), additionally keep only personas that have it active.
  const filteredPersonas: PersonaAPIData[] = filterByAgentScope(
    (personaList ?? []) as PersonaAPIData[],
    scope,
    currentUser?.id,
  ).filter(
    (persona: PersonaAPIData) =>
      !requiredToolkitName ||
      (persona.config_toolkits ?? []).some(
        (toolkit: Toolkit) =>
          toolkit.name === requiredToolkitName && !toolkit.deleted,
      ),
  );

  // Sort personas
  const sortedPersonas = [...filteredPersonas].sort(
    (a: PersonaAPIData, b: PersonaAPIData) => {
      let compareValue = 0;
      if (sortBy === "name") {
        compareValue = (a.name || "").localeCompare(b.name || "");
      } else if (sortBy === "stars") {
        // Sort by star count, with starred by user first, then by star count
        const aStarred = a.is_starred_by_user ? 1 : 0;
        const bStarred = b.is_starred_by_user ? 1 : 0;
        if (aStarred !== bStarred) {
          // User's starred items first when sorting descending
          compareValue = aStarred - bStarred;
        } else {
          // Then by total star count
          compareValue = (a.star_count ?? 0) - (b.star_count ?? 0);
        }
      } else {
        const aDate = new Date(a.create_date || 0).getTime();
        const bDate = new Date(b.create_date || 0).getTime();
        compareValue = aDate - bDate;
      }

      return sortOrder === "asc" ? compareValue : -compareValue;
    },
  );

  const handleCancel = () => {
    onClose();
  };

  const handleSelectPersona = (persona: PersonaAPIData | null) => {
    onConfirm(persona);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      data-testid="aa-persona-selector-modal"
      data-attr="assistant:modal:persona-selector"
      PaperProps={{
        sx: {
          height: isMobile ? "100vh" : "80vh",
          maxHeight: isMobile ? "100vh" : "800px",
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight="bold">
            Select Agent
          </Typography>
          <IconButton
            onClick={handleCancel}
            size="small"
            data-attr="assistant:btn:close-persona-selector"
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Search and Action Bar */}
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={isMobile ? 1 : 2}
          alignItems="stretch"
          sx={{ p: 2, pb: 1 }}
        >
          <TextField
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            data-testid="aa-persona-selector-search"
            data-attr="assistant:input:search-personas"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Box sx={{ px: 2, pb: 1 }}>
          <AgentScopeToggle
            value={scope}
            onChange={setScope}
            dataAttrPrefix="assistant"
            fullWidth={isMobile}
          />
        </Box>

        {/* Sort Controls */}
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={isMobile ? 1 : 2}
          alignItems={isMobile ? "stretch" : "center"}
          sx={{ px: 2, pb: 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flex={1}>
            {isMobile && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 40 }}
              >
                Sort:
              </Typography>
            )}
            {!isMobile && (
              <Typography variant="body2" color="text.secondary">
                Sort by:
              </Typography>
            )}
            <ToggleButtonGroup
              value={sortBy}
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) setSortBy(newValue);
              }}
              size="small"
              fullWidth={isMobile}
            >
              <ToggleButton
                value="stars"
                data-attr="assistant:toggle:sort-personas-by-stars"
              >
                <Tooltip title="Sort by Stars (favorites first)">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Star fontSize="small" />
                    {!isMobile && (
                      <Typography variant="caption">Stars</Typography>
                    )}
                  </Stack>
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="name"
                data-attr="assistant:toggle:sort-personas-by-name"
              >
                <Tooltip title="Sort by Name">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <SortByAlpha fontSize="small" />
                    {!isMobile && (
                      <Typography variant="caption">Name</Typography>
                    )}
                  </Stack>
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="date"
                data-attr="assistant:toggle:sort-personas-by-date"
              >
                <Tooltip title="Sort by Date">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarToday fontSize="small" />
                    {!isMobile && (
                      <Typography variant="caption">Date</Typography>
                    )}
                  </Stack>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton
              size="small"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              data-attr="assistant:btn:toggle-persona-sort-order"
            >
              <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
                {sortOrder === "asc" ? (
                  <ArrowUpward fontSize="small" />
                ) : (
                  <ArrowDownward fontSize="small" />
                )}
              </Tooltip>
            </IconButton>
          </Stack>
        </Stack>

        {(debouncedSearchQuery || scope !== "all") && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing: {sortedPersonas.length} result
              {sortedPersonas.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        )}

        <Divider />

        {/* Persona List */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            minHeight: 0, // Important for flex child scrolling
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Loading agents...
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }} data-testid="aa-persona-selector-list">
              {/* None option — hidden when a toolkit is required, since the
                  default AtomAssist has no toolkit and would fail the filter.
                  Rendered even when the search or scope matches no agent, so
                  the active agent can always be cleared. */}
              {!requiredToolkitName && (
                <ListItem
                  disablePadding
                  data-testid="aa-persona-selector-none"
                  data-attr="assistant:btn:select-no-persona"
                >
                  <ListItemButton
                    selected={selectedPersona === null}
                    onClick={() => handleSelectPersona(null)}
                    sx={{
                      py: 1.5,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Deselect />
                    </ListItemIcon>
                    <ListItemText
                      primary="None"
                      secondary="Use default AtomAssist without an agent"
                      primaryTypographyProps={{
                        fontWeight: "medium",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {sortedPersonas.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 160,
                  }}
                  data-testid="aa-persona-selector-empty"
                >
                  <Typography variant="body2" color="text.secondary">
                    No agents found
                  </Typography>
                </Box>
              )}

              {/* Persona list */}
              {sortedPersonas.map((persona: PersonaAPIData) => (
                <ListItem
                  key={persona.id}
                  disablePadding
                  data-testid={`aa-persona-selector-item-${persona.id}`}
                  data-attr={`assistant:btn:select-persona-${persona.slug || persona.id}`}
                >
                  <ListItemButton
                    selected={selectedPersona?.id === persona.id}
                    onClick={() => handleSelectPersona(persona)}
                    sx={{
                      py: 1.5,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <AssistantOutlined />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <span>{persona.name}</span>
                          {persona.is_starred_by_user && (
                            <Star
                              sx={{ fontSize: 14, color: "warning.main" }}
                            />
                          )}
                          {!persona.is_starred_by_user &&
                            (persona.star_count ?? 0) > 0 && (
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.25}
                                sx={{ color: "text.disabled" }}
                              >
                                <StarBorder sx={{ fontSize: 12 }} />
                                <Typography variant="caption">
                                  {persona.star_count}
                                </Typography>
                              </Stack>
                            )}
                        </Stack>
                      }
                      secondary={persona.description}
                      primaryTypographyProps={{
                        fontWeight: "medium",
                        component: "div",
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleCancel}
          color="inherit"
          fullWidth={isMobile}
          data-testid="aa-persona-selector-cancel"
          data-attr="assistant:btn:cancel-persona-selection"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonaSelectorModal;
